import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Type guard: returns true when an RTK Query error is an HTTP 403 Forbidden.
 * Useful for showing "Access Denied" UI instead of generic error messages.
 */
export function isForbiddenError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as FetchBaseQueryError).status === 403
  );
}

type ApiErrorBody = {
  message?: string;
  error?: string;
};

/**
 * Extract a user-facing message from RTK Query / fetch errors.
 * transformer_api 422 responses encode a plain JSON string (e.g. "unable to save schedule: ...").
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error !== 'object' || error === null) {
    return fallback;
  }

  if ('data' in error) {
    const { data } = error as FetchBaseQueryError;
    if (typeof data === 'string' && data.trim()) {
      return data;
    }
    if (typeof data === 'object' && data !== null) {
      const body = data as ApiErrorBody;
      if (body.message) return body.message;
      if (body.error) return body.error;
    }
  }

  if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
    const { message } = error as { message: string };
    if (message.trim()) return message;
  }

  if ('status' in error && typeof (error as FetchBaseQueryError).status === 'number') {
    return `Request failed with status ${(error as FetchBaseQueryError).status}`;
  }

  return fallback;
}
